import React, { useEffect, useRef, useState } from 'react';
import type { AuthorProfile } from './types';
import { authorDisplayFields, authorLine } from './communityUtils';
import { CommunityDevPath } from './CommunityDevUi';

function ProfilePopover({ author, onClose }: { author: AuthorProfile; onClose: () => void }) {
  const fields = authorDisplayFields(author);

  return (
    <div className="dcf-author-popover" role="dialog" aria-label="Профиль участника">
      <p className="dcf-author-popover__title">Профиль участника</p>
      <dl className="dcf-author-popover__list">
        <div className="dcf-author-popover__row">
          <dt>Псевдоним</dt>
          <dd>{fields.pseudonym}</dd>
        </div>
        <div className="dcf-author-popover__row">
          <dt>Индустрия</dt>
          <dd>{fields.industry}</dd>
        </div>
        <div className="dcf-author-popover__row">
          <dt>Сектор</dt>
          <dd>{fields.sector}</dd>
        </div>
        <div className="dcf-author-popover__row">
          <dt>Должность</dt>
          <dd>{fields.position}</dd>
        </div>
        {fields.company && (
          <div className="dcf-author-popover__row">
            <dt>Компания</dt>
            <dd>{fields.company}</dd>
          </div>
        )}
      </dl>
      {author.experienceRange && (
        <p className="dcf-author-popover__note">Опыт: {author.experienceRange}</p>
      )}
      <button type="button" className="dcf-author-popover__close" onClick={onClose}>
        Закрыть
      </button>
    </div>
  );
}

export function AuthorBadge({
  author,
  devPath,
  preview = false,
}: {
  author: AuthorProfile;
  devPath?: string;
  preview?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || preview) return;
    const onPointerDown = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, preview]);

  if (preview) {
    return (
      <div className="dcf-author-trigger dcf-author-trigger--preview">
        <span className="dcf-author__name">{author.pseudonym}</span>
        <span className="dcf-author__meta">{authorLine(author)}</span>
      </div>
    );
  }

  return (
    <div className="dcf-author-popover-wrap" ref={wrapRef}>
      <button
        type="button"
        className="dcf-author-trigger"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span className="dcf-author__name">
          {author.pseudonym}
          {devPath ? <CommunityDevPath path={devPath} inline /> : null}
        </span>
        <span className="dcf-author__meta">
          {authorLine(author)}
          {author.experienceRange ? ` · ${author.experienceRange}` : ''}
        </span>
      </button>
      {open && <ProfilePopover author={author} onClose={() => setOpen(false)} />}
    </div>
  );
}

export function TopicSummaryBlock({
  body,
  preparedBy,
}: {
  body: string;
  preparedBy: 'moderator' | 'datacircle';
}) {
  return (
    <div className="dcf-summary-block">
      <div className="dcf-summary-block__label">Сводка обсуждения</div>
      <div className="dcf-summary-block__title">Ключевой вывод</div>
      <p className="dcf-summary-block__body">{body}</p>
      <p className="dcf-summary-block__meta">
        {preparedBy === 'datacircle' ? 'Сводка datacircle' : 'Подготовлено модератором'}
      </p>
    </div>
  );
}
